import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLazyQuery, useMutation } from "@apollo/client";
import { alpha, Box, Button, Icon } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { REMOVE_SITE_USER } from "../../../../../graphql/mutations";
import UPDATE_WORKSPACE_ROLE from "../../../../../graphql/mutations/CompanyAdminContext/CompanyWorkspaces/updateSiteUser";
import { UPDATE_SITEUSER_PAYMENTLIABILITY } from "../../../../../graphql/mutations/SiteUsers";
import {
  GET_WORKSPACE_ROLES,
  GET_WORKSPACE_USERS,
} from "../../../../../graphql/queries";
import logger from "../../../../../helpers/logger";
import { useSnackbar } from "../../../../../hooks";
import CustomSelect from "../../../../common/CustomSelect";
import { SearchTextInput } from "../../../../common/SearchTextInput";
import InviteUsersModals from "../../../../SiteUsers/InviteUserModals";
import ManageRolesModal from "../../../../SiteUsers/ManageRolesModal";
import SiteUsersTable from "../../../../SiteUsers/SiteUsersTable";
import { useAlert } from "react-alert";
import PaymentLiabilityModal from "../../../../SiteUsers/PaymentLiabilityModal";

const useStyles = makeStyles(theme => ({
  paperHead: {
    padding: theme.spacing(2),
    borderBottom: "1px solid #ddd",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  btn: {
    padding: "10px 15px",
    fontWeight: 600,
    lineHeight: 0.5,
    color: theme.palette.primary.contrastText,
    fontSize: 12,
    backgroundColor: theme.palette.primary.main,
    minWidth: "unset",
    borderRadius: 4,
    marginLeft: 10,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.8),
    },
    width: "auto",
    [theme.breakpoints.down("sm")]: {
      marginTop: 10,
      marginBottom: 0,
      marginLeft: 0,
    },
  },
  btnIcon: {
    fontSize: 12,
    marginLeft: 10,
    width: "unset",
    height: "unset",
    overflow: "unset",
    verticalAlign: "middle",
  },
  selectWrap: {
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      marginTop: 30,
    },
  },
  popper: {
    width: 280,
  },
  btnDisabled: {
    opacity: 0.8,
    "&.$Mui-disabled": {
      color: theme.palette.primary.contrastText,
    },
  },
  select: {
    marginLeft: 7,
    minWidth: 160,
  },
  searchTextInput: {
    marginLeft: 7,
    marginTop: 0,
    minWidth: 300,
  },
}));

const WorkspaceUsers = ({ siteId }) => {
  const classes = useStyles();
  const intl = useIntl();
  const snackbar = useSnackbar();

  const filterOptions = useMemo(
    () => ([
      { label: intl.formatMessage({ id: "siteUsers.showAllUsers" }), value: "SHOW_ALL", },
      { label: intl.formatMessage({ id: "siteUsers.showInvitedUsers", }), value: "SHOW_INVITED", },
      { label: intl.formatMessage({ id: "siteUsers.showActiveUsers", }), value: "SHOW_ACTIVE", },
      { label: intl.formatMessage({ id: "siteUsers.showInactiveUsers", }), value: "SHOW_INACTIVE", },
    ]),
    [intl]
  );

  const [ascending, setAscending] = useState(true);
  const [filter, setFilter] = useState("SHOW_ALL");
  const [searchTerm, setSearchTerm] = useState();
  const [siteRoleOptions, setSiteRoleOptions] = useState([]);
  const [sortfield, setSortfield] = useState("EMAIL");

  const alert = useAlert();

  var siteRolesRights = {
    canInviteUsers: true,
    canManageSite: true,
    canManageSiteRoles: true,
  };

  const [siteUsers, setSiteUsers] = useState([]);
  const [siteUsersError, setSiteUsersError] = useState(false);
  const [siteUsersLoading, setSiteUsersLoading] = useState(false);

  const [
    listWorkspaceUsers,
    { data: siteUsersData, loading: loadingUsers, error: errorUsers },
  ] = useLazyQuery(GET_WORKSPACE_USERS, {
    variables: {
      ascending,
      filter,
      searchTerm,
      siteId,
      sortfield,
    },
  });

  const [openInviteModals, setOpenInviteModals] = useState(false);

  const handleOpenInviteUser = () => {
    setOpenInviteModals(true);
  };

  const handleCloseInviteUser = () => {
    setOpenInviteModals(false);
  };

  const [openManageRoles, setOpenManageRoles] = useState(false);

  const handleOpenManageRoles = () => {
    setOpenManageRoles(true);
  };

  const [
    listWorkspaceRoles,
    { data: siteRolesData, loadingRoles, errorRoles },
  ] = useLazyQuery(GET_WORKSPACE_ROLES, {
    variables: {
      siteId,
    },
  });

  const getSiteUsers = () => {
    listWorkspaceUsers();
  };

  useEffect(() => {
    if (siteId) {
      listWorkspaceUsers();
      listWorkspaceRoles();
    }
  }, [siteId]);

  useEffect(() => {
    if (
      siteUsersData &&
      siteUsersData.companyAdminContext &&
      siteUsersData.companyAdminContext.companyWorkspaces &&
      Array.isArray(
        siteUsersData.companyAdminContext.companyWorkspaces.listSiteUsersForSite
      )
    ) {
      setSiteUsers(
        siteUsersData.companyAdminContext.companyWorkspaces.listSiteUsersForSite
      );
    }
  }, [siteUsersData]);

  useEffect(() => {
    if (
      siteRolesData &&
      siteRolesData.companyAdminContext &&
      siteRolesData.companyAdminContext.companyWorkspaces &&
      Array.isArray(
        siteRolesData.companyAdminContext.companyWorkspaces.listSiteRolesForSite
      )
    ) {
      setSiteRoleOptions(
        siteRolesData.companyAdminContext.companyWorkspaces.listSiteRolesForSite
      );
    }
  }, [siteRolesData]);

  useEffect(() => {
    setSiteUsersLoading(loadingUsers);
  }, [loadingUsers]);

  useEffect(() => {
    if (errorUsers) {
      setSiteUsersError(errorUsers);
    }
  }, [errorUsers]);

  const [removeSiteUser] = useMutation(REMOVE_SITE_USER);
  const [updateSiteUser] = useMutation(UPDATE_WORKSPACE_ROLE);

  const handleRemoveSiteUser = async (siteId, id) => {
    try {
      const res = await removeSiteUser({ variables: { siteId, id } });

      if (
        res &&
        res.data &&
        res.data.companyAdminContext &&
        res.data.companyAdminContext.companyWorkspaces &&
        res.data.companyAdminContext.companyWorkspaces.removeSiteUser &&
        res.data.companyAdminContext.companyWorkspaces.removeSiteUser.success
      ) {
        getSiteUsers();
        alert.success(<FormattedMessage id="siteUsers.removedUser" />);
      } else {
        snackbar.workspaceError(
          <FormattedMessage id="siteUsers.errorRemoveUser" />
        );
      }
    } catch (err) {
      snackbar.workspaceError(
        <FormattedMessage id="siteUsers.errorRemoveUser" />
      );
      logger.error(`Error in removing site user. Unable to send ${err}`);
    }
  };


  const [updatePaymentLiability] = useMutation(
    UPDATE_SITEUSER_PAYMENTLIABILITY,
  );

  const [paymentLiabilityUser, setPaymentLiabilityUser] = useState();

  const handleOpenPaymentLiabiltyModal = (siteUser) => {
    setPaymentLiabilityUser(siteUser);
  }

  const handleUpdatePaymentLiability = async (
    user,
    editCompanyId,
    subscriptionLevel,
  ) => {
    try {
      const res = await updatePaymentLiability({
        variables: {
          input: {
            id: user.id,
            siteId: user.siteId,
            editingCompanyId: editCompanyId?.companyId,
            subscriptionLevel,
          },
        },
      });

      if (
        res?.data?.siteUserContext?.updateSiteUserPaymentLiability
          ?.success
      ) {
        const updatedUser =
          res.data.siteUserContext.updateSiteUserPaymentLiability
            .siteUser;

        setSiteUsers(prev =>
          prev.map(user =>
            user.id === updatedUser.id ? updatedUser : user
          )
        );


      } else {
        snackbar.workspaceError(
          <FormattedMessage id="siteUsers.errorUpdate" />,
        );
      }

      setPaymentLiabilityUser(null);
    } catch (err) {
      snackbar.workspaceError(
        <FormattedMessage id="siteUsers.errorUpdate" />,
      );
      logger.error(
        `Error in updating site user. Unable to send ${err}`,
      );
    }
  };

  const handleUpdateSiteUser = async params => {
    try {
      const res = await updateSiteUser({ variables: { siteUser: params } });

      if (
        res &&
        res.data &&
        res.data.companyAdminContext &&
        res.data.companyAdminContext.companyWorkspaces &&
        res.data.companyAdminContext.companyWorkspaces.updateSiteUserRole &&
        res.data.companyAdminContext.companyWorkspaces.updateSiteUserRole
          .success
      ) {
        alert.success(<FormattedMessage id="siteUsers.update" />);
      } else {
        snackbar.workspaceError(
          <FormattedMessage id="siteUsers.errorUpdate" />
        );
      }
    } catch (err) {
      snackbar.workspaceError(<FormattedMessage id="siteUsers.errorUpdate" />);
      logger.error(`Error in updating site user. Unable to send ${err}`);
    }
  };

  return (
    <>
      <Box className={classes.paperHead}>
        {siteRolesRights.canInviteUsers && (
          <Button
            className={classes.btn}
            classes={{ disabled: classes.btnDisabled }}
            onClick={handleOpenInviteUser}
          >
            <FormattedMessage id="btn.inviteUsers" />
            <Icon className={clsx(["fa fa-user-plus", classes.btnIcon])} />
          </Button>
        )}

        {siteRolesRights.canManageSiteRoles && (
          <Button
            className={classes.btn}
            classes={{ disabled: classes.btnDisabled }}
            onClick={handleOpenManageRoles}
          >
            <FormattedMessage id="btn.manageRoles" />
            <Icon className={clsx(["fa fa-user-cog", classes.btnIcon])} />
          </Button>
        )}

        <Box className={classes.selectWrap}>
          <CustomSelect
            customClass={classes.select}
            inputLabel={<FormattedMessage id="siteUsers.filter" />}
            onChange={e => setFilter(e.target.value)}
            options={filterOptions}
            value={filter}
          />
          <SearchTextInput
            className={classes.searchTextInput}
            onChange={setSearchTerm}
            searching={loadingUsers}
          />
        </Box>
      </Box>

      <SiteUsersTable
        fetchSiteUsers={getSiteUsers}
        siteRoleOptions={siteRoleOptions}
        sortfield={sortfield}
        ascending={ascending}
        setSortfield={setSortfield}
        setAscending={setAscending}
        siteRolesRights={siteRolesRights}
        siteUsers={siteUsers}
        siteUsersLoading={siteUsersLoading}
        onRemoveSiteUser={handleRemoveSiteUser}
        onUpdateSiteUser={handleUpdateSiteUser}
        onOpenPaymentLiabilityModal={handleOpenPaymentLiabiltyModal }
      />

      {openManageRoles && (
        <ManageRolesModal
          open={openManageRoles}
          setOpenManageRoles={setOpenManageRoles}
          selectedSite={siteId}
          siteRoleOptions={siteRoleOptions}
        />
      )}

      
      <PaymentLiabilityModal user={paymentLiabilityUser} onClose={() => setPaymentLiabilityUser()} onSave={handleUpdatePaymentLiability} />

      <InviteUsersModals
        open={openInviteModals}
        onCloseDialog={handleCloseInviteUser}
        selectedSite={siteId}
        siteRoleOptions={siteRoleOptions}
        getSiteUsers={getSiteUsers}
        setSiteSelect={() => { }}
      />
    </>
  );
};

export default WorkspaceUsers;
